import { DecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import { addClassNamesToElement } from "@lexical/utils";
import { serviceURL } from "../../../../utils/service-url";
import { imageURL } from "../../../design-huddle/utils";
import { headlessDecorate } from "./headless-decorate";
import { convertDesignHuddleImageElement, isDesignHuddleElement } from "./utils";

import type { UUID } from "../../../../utils/uuid";
import type { SerializedDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import type {
  DOMConversionMap,
  DOMExportOutput,
  ElementFormatType,
  LexicalNode,
  NodeKey,
  Spread,
} from "lexical";

export const DefaultProportionOfMaxWidth = 0.6666;
export type SerializedDesignHuddleNode =
  | Spread<
      {
        version: 1;
        type: "design-huddle";
        id?: string;
        account_id: string;
        page_number?: number;
        dimensions?: { height: number; width: number };
        customisations?: DesignHuddleCustomisations;
        cacheBustNumber: number;
        mustBeCloned?: boolean;
      },
      SerializedDecoratorBlockNode
    >
  | Spread<
      {
        version: 2;
        type: "design-huddle";
        id?: string;
        account_id: string;
        page_number?: number;
        proportionOfMaxWidth?: number;
        aspect?: number;
        customisations?: DesignHuddleCustomisations;
        cacheBustNumber: number;
        mustBeCloned?: boolean;
        href?: string;
        page_id?: string;
        error?: boolean;
        sourceAssetId?: UUID;
        contentType?: "video" | "image";
        slackAppVideoUrl?: string;
        title?: string;
      },
      SerializedDecoratorBlockNode
    >;

export interface DesignHuddleCustomisations {
  templateCode: string;
  mergeTags: string[];
}

export class DesignHuddleNode extends DecoratorBlockNode {
  __id?: string;
  __account_id: string;
  __cacheBustNumber: number;
  __page_number?: number;
  __page_id?: string;
  __proportionOfMaxWidth: number;
  __aspect: number;
  __customisations?: DesignHuddleCustomisations;
  __must_be_cloned?: boolean;
  __url?: string;
  __preview_url?: string;
  __href?: string;
  __error?: boolean;
  __sourceAssetId?: UUID;
  __checkTags?: boolean;
  __contentType: "video" | "image";
  __slackAppVideoUrl?: string;
  __title?: string;

  static getType(): string {
    return "design-huddle";
  }

  static clone(node: DesignHuddleNode): DesignHuddleNode {
    return new this(
      node.__account_id,
      node.__id,
      node.__page_number,
      node.__proportionOfMaxWidth,
      node.__aspect,
      node.__customisations,
      node.__format,
      node.__key,
      node.__cacheBustNumber,
      Boolean(node.__id) || node.__must_be_cloned, // Always clone existing projects when using copy/paste
      node.__href,
      node.__page_id,
      node.__preview_url,
      node.__error,
      node.__sourceAssetId,
      node.__checkTags,
      node.__contentType,
      node.__slackAppVideoUrl,
      node.__title,
    );
  }

  constructor(
    account_id: string,
    id?: string,
    page_number?: number,
    proportionOfMaxWidth: number = DefaultProportionOfMaxWidth,
    aspect: number = 1,
    customisations?: DesignHuddleCustomisations,
    format?: ElementFormatType,
    key?: NodeKey,
    cacheBustNumber: number = 1,
    mustBeCloned?: boolean,
    href?: string,
    page_id?: string,
    preview_url?: string,
    error?: boolean,
    sourceAssetId?: UUID,
    checkTags: boolean = false,
    contentType: "video" | "image" = "image",
    slackAppVideoUrl?: string,
    title?: string,
  ) {
    super(format, key);

    this.__id = id;
    this.__account_id = account_id;
    this.__page_number = page_number;
    this.__proportionOfMaxWidth = proportionOfMaxWidth;
    this.__aspect = aspect;
    this.__customisations = customisations;
    this.__cacheBustNumber = cacheBustNumber;
    this.__must_be_cloned = mustBeCloned;
    this.__href = href;
    this.__page_id = page_id;
    this.__preview_url = preview_url;
    this.__error = error;
    this.__sourceAssetId = sourceAssetId;
    this.__checkTags = checkTags;
    this.__contentType = contentType;
    this.__slackAppVideoUrl = slackAppVideoUrl;
    this.__title = title;
  }

  setError(error: boolean): void {
    const writable = this.getWritable();
    writable.__error = error;
  }

  setProjectId(id: string): void {
    const writable = this.getWritable();
    writable.__id = id;
  }

  setHref(href?: string): void {
    const writable = this.getWritable();
    writable.__href = href;
  }

  getHref(): string | undefined {
    return this.__href;
  }

  setPageNumber(page_number?: number): void {
    const writable = this.getWritable();
    writable.__page_number = page_number;
  }

  templateChangeUpdate(customisations?: DesignHuddleCustomisations): void {
    const writable = this.getWritable();
    writable.__customisations = customisations;
    writable.__cacheBustNumber = this.__cacheBustNumber + 1;
    writable.__checkTags = false;
  }

  setMustBeCloned(mustBeCloned?: boolean): void {
    const writable = this.getWritable();
    writable.__must_be_cloned = mustBeCloned;
  }

  createDOM(): HTMLElement {
    const dom = document.createElement("span");
    addClassNamesToElement(dom, "decorator-block-node");
    return dom;
  }

  setSize(proportionOfMaxWidth: number, aspect: number): void {
    const writable = this.getWritable();
    writable.__proportionOfMaxWidth = proportionOfMaxWidth;
    writable.__aspect = aspect;
  }

  getAspect(): number {
    return this.__aspect;
  }

  setPreviewUrl(url?: string): void {
    const writable = this.getWritable();
    writable.__preview_url = url;
  }

  setPageId(id?: string): void {
    // When inserting from the content library we have the page id but not the page number
    const writable = this.getWritable();
    writable.__page_id = id;
  }

  setSourceAssetId(id?: UUID): void {
    const writable = this.getWritable();
    writable.__sourceAssetId = id;
  }

  setTitle(title: string | undefined): void {
    const writable = this.getWritable();
    writable.__title = title;
  }

  setSlackAppVideoUrl(url: string): void {
    const writable = this.getWritable();
    writable.__slackAppVideoUrl = url;
  }

  static importJSON(serializedNode: SerializedDesignHuddleNode): DesignHuddleNode {
    if (serializedNode.version === 1) {
      return new this(
        serializedNode.account_id,
        serializedNode.id,
        serializedNode.page_number,
        Math.min(1, (serializedNode.dimensions?.width ?? 600) / 600),
        (serializedNode.dimensions?.height ?? 400) / (serializedNode.dimensions?.width ?? 600),
        serializedNode.customisations,
        serializedNode.format,
        undefined,
        serializedNode.cacheBustNumber,
        serializedNode.mustBeCloned,
      );
    }
    return new this(
      serializedNode.account_id,
      serializedNode.id,
      serializedNode.page_number,
      serializedNode.proportionOfMaxWidth,
      serializedNode.aspect,
      serializedNode.customisations,
      serializedNode.format,
      undefined,
      serializedNode.cacheBustNumber,
      serializedNode.mustBeCloned,
      serializedNode.href,
      serializedNode.page_id,
      undefined,
      serializedNode.error,
      serializedNode.sourceAssetId,
      undefined,
      serializedNode.contentType,
      serializedNode.slackAppVideoUrl,
      serializedNode.title,
    );
  }

  exportJSON(): SerializedDesignHuddleNode {
    return {
      id: this.__id,
      cacheBustNumber: this.__cacheBustNumber,
      account_id: this.__account_id,
      page_number: this.__page_number,
      proportionOfMaxWidth: this.__proportionOfMaxWidth,
      aspect: this.__aspect,
      type: "design-huddle",
      version: 2,
      format: this.__format,
      customisations: this.__customisations,
      mustBeCloned: this.__must_be_cloned,
      href: this.__href,
      page_id: this.__page_id,
      error: this.__error,
      sourceAssetId: this.__sourceAssetId,
      contentType: this.__contentType,
      slackAppVideoUrl: this.__slackAppVideoUrl,
      title: this.__title,
    };
  }

  decorate(): React.JSX.Element {
    return headlessDecorate();
  }

  exportDOM(): DOMExportOutput {
    const dom = document.createElement("img");
    dom.src = imageURL(this.__account_id, this.__id ?? "", this.__cacheBustNumber);
    dom.style.width = `${(this.__proportionOfMaxWidth ?? 1) * 100}%`;
    dom.style.aspectRatio = `${this.__aspect ?? 1}`;
    dom.setAttribute("data-node-type", "design-huddle-node");
    dom.setAttribute("data-dh-account-id", this.__account_id);
    dom.setAttribute("data-dh-content-type", this.__contentType);
    this.__href && dom.setAttribute("data-dh-href", this.__href);
    this.__sourceAssetId && dom.setAttribute("data-dh-asset-id", this.__sourceAssetId);
    dom.setAttribute("data-dh-check-tags", this.__contentType);
    dom.setAttribute(
      "data-dh-customisations",
      this.__customisations ? JSON.stringify(this.__customisations) : "",
    );
    this.__id && dom.setAttribute("data-dh-project-id", this.__id);
    this.__title && dom.setAttribute("data-dh-title", this.__title);
    return {
      element: dom,
    };
  }

  setNewUrl(url: string): void {
    const writable = this.getWritable();
    writable.__url = url;
  }

  getDesignHuddlePreviewUrl(maxWidth?: number): string | null {
    if (!this.__customisations) {
      return null;
    }
    const cacheBustParam = `v=${this.__cacheBustNumber}`;
    const params =
      this.__customisations.mergeTags.length > 0
        ? `?${this.__customisations.mergeTags
            .map((t) => `${t}={{${t}}}`)
            .join("&")}&${cacheBustParam}`
        : `?${cacheBustParam}`;
    const width = maxWidth ? `&width=${Math.floor(maxWidth * this.__proportionOfMaxWidth)}` : "";
    return `${serviceURL("huddle")}template/${this.__customisations.templateCode}/preview${params}${width}`;
  }

  getImageUrl(): string {
    return imageURL(this.__account_id, this.__id!, this.__cacheBustNumber);
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: HTMLElement) => {
        if (!isDesignHuddleElement(node)) return null;

        return {
          conversion: (n) => convertDesignHuddleImageElement(n, DesignHuddleNode),
          priority: 0,
        };
      },
    };
  }
}

export function $isDesignHuddleNode(
  node: LexicalNode | null | undefined,
): node is DesignHuddleNode {
  return node instanceof DesignHuddleNode;
}

// Lives here rather than alongside the DesignHuddleNodeMui variant so callers that must not pull in
// MUI, such as the api worker, can still build an asset node.
export function $createDesignHuddleNodeFromAssetOfClass(
  DesignHuddle: typeof DesignHuddleNode,
  account_id: string,
  project_id?: string,
  proportionOfMaxWidth?: number,
  aspect: number = 1,
  page_id?: string,
  preview_url?: string,
  asset_id?: UUID,
  check_tags: boolean = false,
): DesignHuddleNode {
  return new DesignHuddle(
    account_id,
    project_id,
    undefined,
    proportionOfMaxWidth,
    aspect,
    undefined,
    undefined,
    undefined,
    undefined,
    true,
    undefined,
    page_id,
    preview_url,
    undefined,
    asset_id,
    check_tags,
  );
}
